// ============================================================================
//  notify.js — global notification helper backed by SweetAlert2.
// ----------------------------------------------------------------------------
//  Import once, use anywhere:
//    import notify from '../Notify/notify';
//    notify.success('Facility saved');
//    notify.error('Something went wrong', 'Try again in a moment');
//    notify.toast.success('Copied to clipboard');
//    const ok = await notify.confirm('Delete this user?');
//
//  All styling is driven by the active theme's CSS variables, so it swaps
//  colours automatically when the user changes themes.
// ============================================================================

import Swal from 'sweetalert2';
import './notify.css';

const baseOptions = {
  buttonsStyling: false,
  reverseButtons: true,
  showClass: { popup: 'gigatt-swal-show' },
  hideClass: { popup: 'gigatt-swal-hide' },
  customClass: {
    popup: 'gigatt-swal',
    title: 'gigatt-swal__title',
    htmlContainer: 'gigatt-swal__body',
    confirmButton: 'btn btn-primary',
    cancelButton: 'btn btn-ghost',
    denyButton: 'btn btn-ghost',
    actions: 'gigatt-swal__actions',
    icon: 'gigatt-swal__icon',
  },
};

const modal = Swal.mixin(baseOptions);

const Toast = Swal.mixin({
  ...baseOptions,
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3200,
  timerProgressBar: true,
  customClass: {
    ...baseOptions.customClass,
    popup: 'gigatt-swal gigatt-toast',
  },
  didOpen: (el) => {
    el.addEventListener('mouseenter', Swal.stopTimer);
    el.addEventListener('mouseleave', Swal.resumeTimer);
  },
});

function pickText(input) {
  if (input && typeof input === 'object' && 'message' in input) return input.message;
  if (typeof input === 'string') return input;
  return '';
}

const notify = {
  success(title, text) {
    return modal.fire({ icon: 'success', title, text, confirmButtonText: 'OK' });
  },
  error(title, text) {
    return modal.fire({
      icon: 'error',
      title: title || 'Something went wrong',
      text: pickText(text),
      confirmButtonText: 'Dismiss',
    });
  },
  warning(title, text) {
    return modal.fire({ icon: 'warning', title, text, confirmButtonText: 'Got it' });
  },
  info(title, text) {
    return modal.fire({ icon: 'info', title, text, confirmButtonText: 'OK' });
  },

  async confirm(title, text, opts = {}) {
    const res = await modal.fire({
      icon: opts.icon || 'question',
      title,
      text,
      showCancelButton: true,
      confirmButtonText: opts.confirmText || 'Yes',
      cancelButtonText: opts.cancelText || 'Cancel',
      focusCancel: opts.focusCancel !== false,
    });
    return res.isConfirmed;
  },

  // Text prompt — resolves to the entered string, or `null` if cancelled.
  async prompt(title, text, opts = {}) {
    const res = await modal.fire({
      icon: opts.icon || 'question',
      title,
      text,
      input: opts.multiline ? 'textarea' : 'text',
      inputPlaceholder: opts.placeholder || '',
      inputValue: opts.defaultValue || '',
      inputValidator: opts.required
        ? (value) => (value && value.trim().length >= (opts.minLength || 1)) ? null : (opts.requiredMessage || 'Please enter a value')
        : undefined,
      showCancelButton: true,
      confirmButtonText: opts.confirmText || 'Save',
      cancelButtonText:  opts.cancelText  || 'Cancel',
    });
    return res.isConfirmed ? (res.value || '') : null;
  },

  loading(title = 'Working…', text = '') {
    return modal.fire({
      title,
      text,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
  },

  close() {
    Swal.close();
  },

  toast: {
    success: (title) => Toast.fire({ icon: 'success', title }),
    error:   (title) => Toast.fire({ icon: 'error',   title }),
    warning: (title) => Toast.fire({ icon: 'warning', title }),
    info:    (title) => Toast.fire({ icon: 'info',    title }),
  },
};

export default notify;
